/**
 * 🔒 密码强度验证器
 * 提供统一的密码/PIN 强度检查
 */

// 常见弱密码字典
const WEAK_PASSWORDS = [
  '123456', '1234567', '12345678', '123456789', '1234567890',
  '000000', '111111', '222222', '333333', '444444', '555555',
  '666666', '777777', '888888', '999999',
  'password', 'Password', 'PASSWORD', 'pass', 'admin', 'Admin',
  'qwerty', 'QWERTY', 'asdfgh', 'zxcvbn',
  'abc123', 'ABC123', '123abc', '123ABC',
  '654321', '987654321', '0987654321',
  'test', 'Test', 'demo', 'Demo',
  '112233', '123123', '121212', '131313',
  '010101', '020202', '101010', '202020',
];

// 键盘序列模式
const KEYBOARD_PATTERNS = [
  /qwert/i, /asdfg/i, /zxcvb/i,  // QWERTY 键盘
  /yuiop/i, /hjkl/i, /bnm/i,      // QWERTY 键盘继续
];

// 日期格式模式（弱密码）
const DATE_PATTERNS = [
  /^\d{4}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/,  // YYYYMMDD
  /^(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{4}$/,  // MMDDYYYY
  /^\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/,  // YYMMDD
];

/**
 * 检查密码是否为弱密码
 * @param {string} password - 要检查的密码
 * @returns {Object} {isWeak: boolean, reason: string|null}
 */
export function checkWeakPassword(password) {
  if (!password) {
    return {isWeak: true, reason: '密码不能为空'};
  }

  // 检查 1: 字典攻击
  if (WEAK_PASSWORDS.includes(password.toLowerCase())) {
    return {isWeak: true, reason: '这是一个常见的弱密码，容易被猜到'};
  }

  // 检查 2: 全是相同字符
  if (/^(.)\1+$/.test(password)) {
    return {isWeak: true, reason: '密码不能全是相同字符（如 111111）'};
  }

  // 检查 3: 顺序数字
  if (/^(0123456|1234567|2345678|3456789|456789|56789|6789|789|89|9)/.test(password)) {
    return {isWeak: true, reason: '密码不能是连续数字（如 123456）'};
  }

  // 检查 4: 倒序数字
  if (/^(9876543|8765432|7654321|654321|54321|4321|321|21|1)/.test(password)) {
    return {isWeak: true, reason: '密码不能是连续倒序数字（如 654321）'};
  }

  // 检查 5: 重复模式
  if (/^(.{1,3})\1+$/.test(password)) {
    return {isWeak: true, reason: '密码不能是简单重复模式（如 121212, 010101）'};
  }

  // 检查 6: 键盘序列
  for (const pattern of KEYBOARD_PATTERNS) {
    if (pattern.test(password)) {
      return {isWeak: true, reason: '密码不能是键盘序列（如 qwerty, asdfgh）'};
    }
  }

  // 检查 7: 日期格式
  for (const pattern of DATE_PATTERNS) {
    if (pattern.test(password)) {
      return {isWeak: true, reason: '密码不能是日期格式（如 19900101）'};
    }
  }

  // 检查 8: 纯数字且过短
  if (/^\d+$/.test(password) && password.length < 6) {
    return {isWeak: true, reason: '纯数字密码建议至少 6 位'};
  }

  return {isWeak: false, reason: null};
}

/**
 * 计算密码强度分数（0-100）
 * @param {string} password - 要检查的密码
 * @returns {number} 强度分数 (0-100)
 */
export function calculatePasswordStrength(password) {
  if (!password) return 0;

  let score = 0;
  const length = password.length;

  // 长度分数（最多 40 分）
  if (length >= 4) score += 10;
  if (length >= 6) score += 10;
  if (length >= 8) score += 10;
  if (length >= 12) score += 10;

  // 字符多样性（最多 60 分）
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSpecial = /[^a-zA-Z0-9]/.test(password);

  if (hasLower) score += 15;
  if (hasUpper) score += 15;
  if (hasDigit) score += 15;
  if (hasSpecial) score += 15;

  // 惩罚弱密码模式
  const {isWeak} = checkWeakPassword(password);
  if (isWeak) {
    score = Math.max(0, score - 50);
  }

  return Math.min(100, score);
}

/**
 * 获取密码强度描述
 * @param {number} score - 强度分数
 * @returns {Object} {level: string, color: string, text: string}
 */
export function getPasswordStrengthLabel(score) {
  if (score < 30) {
    return {level: 'weak', color: 'error', text: '弱'};
  } else if (score < 60) {
    return {level: 'medium', color: 'warning', text: '中等'};
  } else if (score < 80) {
    return {level: 'good', color: 'primary', text: '良好'};
  } else {
    return {level: 'strong', color: 'success', text: '强'};
  }
}

/**
 * 验证 PIN 格式和强度（供表单使用）
 * @param {string} pin - PIN 码
 * @param {Object} options - 选项 {minLength, maxLength, allowWeak}
 * @returns {Object} {valid: boolean, error: string|null, warning: string|null}
 */
export function validatePin(pin, options = {}) {
  const {
    minLength = 4,
    maxLength = 63,
    allowWeak = false,
  } = options;

  // 长度检查
  if (!pin || pin.length < minLength) {
    return {
      valid: false,
      error: `PIN 长度至少 ${minLength} 个字符`,
      warning: null,
    };
  }

  if (pin.length > maxLength) {
    return {
      valid: false,
      error: `PIN 长度最多 ${maxLength} 个字符`,
      warning: null,
    };
  }

  // 弱密码检查
  const {isWeak, reason} = checkWeakPassword(pin);
  if (isWeak) {
    if (allowWeak) {
      // 允许弱密码，但给警告
      return {
        valid: true,
        error: null,
        warning: `⚠️ ${reason}`,
      };
    } else {
      // 不允许弱密码
      return {
        valid: false,
        error: `${reason}`,
        warning: null,
      };
    }
  }

  return {valid: true, error: null, warning: null};
}

